import { api } from "./api";

// ════════════════════════════════════════════════════════════════════════════
// Appointment Types
// ════════════════════════════════════════════════════════════════════════════

export type AppointmentStatus =
  | "scheduled"
  | "checked_in"
  | "completed"
  | "no_show"
  | "cancelled";

// Front-desk (virtual receptionist) confirmation-call status
export type VrConfirmationStatus =
  | "unconfirmed"
  | "confirmed"
  | "no_answer"
  | "voicemail"
  | "reschedule_requested"
  | "bad_contact";

export interface VrAppointmentNote {
  id: string;
  body: string;
  authorName: string | null;
  createdAt: string;
}

export interface Appointment {
  id: string;
  clientId: string;
  serviceId: string;
  providerId: string;
  locationId: string;
  confirmationCode: string;
  date: string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  notes?: string;
  clientNotes?: string;
  checkedInAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  rescheduleCount: number;
  createdBy: "client" | "staff";
  createdAt: string;
  updatedAt: string;
  campaignLeadId?: string | null;
  campaignName?: string | null;
  vrConfirmationStatus?: VrConfirmationStatus;
  vrConfirmationAttempts?: number;
  vrLastAttemptAt?: string | null;
  client: {
    name: string;
    email?: string;
    phone?: string;
  };
  service: {
    name: string | null;
    durationMinutes: number | null;
    price: string | null;
  } | null;
  provider: {
    name: string | null;
  } | null;
  location: {
    name: string | null;
  } | null;
}

export interface AppointmentDetail extends Appointment {
  rescheduledFromId?: string;
  linkedAppointmentId?: string;
  campaignLeadId?: string | null;
  campaignName?: string | null;
  client: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    allergies?: string;
    profileNotes?: string;
  };
  service: {
    id: string | null;
    name: string | null;
    description?: string;
    durationMinutes: number | null;
    price: string | null;
  } | null;
  provider: {
    id: string | null;
    name: string | null;
    email?: string;
    phone?: string;
  } | null;
  location: {
    id: string | null;
    name: string | null;
    address?: string;
  } | null;
  forms: Array<{
    id: string;
    formName: string;
    signedAt: string;
  }>;
  transactions: Array<{
    id: string;
    type: string;
    amount: string;
    status: string;
    createdAt: string;
  }>;
  vrNotes?: VrAppointmentNote[];
}

export interface CalendarDay {
  date: string;
  total: number;
  scheduled: number;
  checkedIn: number;
  completed: number;
  noShow: number;
  cancelled: number;
}

export interface CalendarData {
  month: string;
  days: CalendarDay[];
  totals: {
    total: number;
    scheduled: number;
    checkedIn: number;
    completed: number;
    noShow: number;
    cancelled: number;
    totalRevenue: string;
  };
}

export interface DashboardStats {
  today: {
    total: number;
    scheduled: number;
    checkedIn: number;
    completed: number;
  };
  weekRevenue: string;
  noShowRate: number;
  newClients: number;
  upcoming: number;
}

// ════════════════════════════════════════════════════════════════════════════
// API Functions
// ════════════════════════════════════════════════════════════════════════════

export interface ListAppointmentsParams {
  startDate?: string;
  endDate?: string;
  providerId?: string;
  locationId?: string;
  status?: AppointmentStatus;
  clientId?: string;
  limit?: number;
  offset?: number;
}

export async function listAppointments(
  params: ListAppointmentsParams = {}
): Promise<{ appointments: Appointment[]; total: number; limit: number; offset: number }> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.set("startDate", params.startDate);
  if (params.endDate) searchParams.set("endDate", params.endDate);
  if (params.providerId) searchParams.set("providerId", params.providerId);
  if (params.locationId) searchParams.set("locationId", params.locationId);
  if (params.status) searchParams.set("status", params.status);
  if (params.clientId) searchParams.set("clientId", params.clientId);
  if (params.limit) searchParams.set("limit", String(params.limit));
  if (params.offset) searchParams.set("offset", String(params.offset));

  const query = searchParams.toString();
  return api<{ appointments: Appointment[]; total: number; limit: number; offset: number }>(
    `/api/appointments${query ? `?${query}` : ""}`
  );
}

export async function getAppointment(id: string): Promise<AppointmentDetail> {
  const response = await api<{ appointment: AppointmentDetail }>(`/api/appointments/${id}`);
  return response.appointment;
}

export interface GetCalendarParams {
  month: string; // YYYY-MM
  providerId?: string;
  locationId?: string;
}

export async function getCalendar(params: GetCalendarParams): Promise<CalendarData> {
  const searchParams = new URLSearchParams();
  searchParams.set("month", params.month);
  if (params.providerId) searchParams.set("providerId", params.providerId);
  if (params.locationId) searchParams.set("locationId", params.locationId);

  return api<CalendarData>(`/api/appointments/calendar?${searchParams.toString()}`);
}

export interface GetDayParams {
  date: string; // YYYY-MM-DD
  providerId?: string;
  locationId?: string;
}

export async function getDayAppointments(
  params: GetDayParams
): Promise<{ date: string; appointments: Appointment[] }> {
  const searchParams = new URLSearchParams();
  searchParams.set("date", params.date);
  if (params.providerId) searchParams.set("providerId", params.providerId);
  if (params.locationId) searchParams.set("locationId", params.locationId);

  return api<{ date: string; appointments: Appointment[] }>(
    `/api/appointments/day?${searchParams.toString()}`
  );
}

export async function getDashboardStats(): Promise<DashboardStats> {
  return api<DashboardStats>("/api/appointments/stats/dashboard");
}

export interface CreateAppointmentData {
  clientId: string;
  serviceId: string;
  providerId: string;
  locationId: string;
  date: string;
  startTime: string;
  notes?: string;
  clientNotes?: string;
}

export async function createAppointment(
  data: CreateAppointmentData
): Promise<Appointment> {
  const response = await api<{ appointment: Appointment }>("/api/appointments", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return response.appointment;
}

export interface UpdateAppointmentData {
  notes?: string;
  clientNotes?: string;
  providerId?: string;
  date?: string;
  startTime?: string;
}

export async function updateAppointment(
  id: string,
  data: UpdateAppointmentData
): Promise<Partial<Appointment>> {
  const response = await api<{ appointment: Partial<Appointment> }>(`/api/appointments/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return response.appointment;
}

export async function updateAppointmentStatus(
  id: string,
  status: AppointmentStatus,
  cancelReason?: string
): Promise<Partial<Appointment>> {
  const response = await api<{ appointment: Partial<Appointment> }>(
    `/api/appointments/${id}/status`,
    {
      method: "PATCH",
      body: JSON.stringify({ status, cancelReason }),
    }
  );
  return response.appointment;
}

